import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import Navbar from "@/components/layout/Navbar";
import { useAuth } from "@/context/AuthContext";
import { Plus, MapPin, Calendar, DollarSign, MessageCircle } from "lucide-react";
import MessageDrawer from "@/components/MessageDrawer";

export default function CustomerDashboard() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [threads, setThreads] = useState([]);
  const [chatJob, setChatJob] = useState(null);

  const load = async () => {
    const r = await api.get("/jobs/my");
    setJobs(r.data);
    const t = await api.get("/messages/threads");
    setThreads(t.data);
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen bg-bone">
      <Navbar />
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-12">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <div className="overline mb-3">// Customer dashboard</div>
            <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tighter">Welcome, {user?.name?.split(" ")[0]}.</h1>
            <p className="text-muted-foreground mt-2">Your active and past projects.</p>
          </div>
          <Link to="/customer/post" className="brut-btn-primary" data-testid="customer-post-job">
            <Plus className="w-4 h-4 mr-1" /> Post a project
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-2 border-ink mb-10">
          <Stat label="Projects" n={jobs.length} />
          <Stat label="Open" n={jobs.filter((j) => j.status === "open").length} />
          <Stat label="Total unlocks" n={jobs.reduce((a, j) => a + (j.contractor_unlocks || 0), 0)} last />
        </div>

        <div className="overline mb-4">// Your projects</div>
        {jobs.length === 0 ? (
          <div className="brut-card p-12 text-center bg-white">
            <p className="font-mono text-sm uppercase tracking-widest">No projects yet</p>
            <Link to="/customer/post" className="brut-btn-primary mt-4 inline-flex" data-testid="empty-post">Post your first one</Link>
          </div>
        ) : (
          <div className="space-y-4">
            {jobs.map((j) => {
              const thread = threads.find((t) => t.job_id === j.job_id);
              return (
                <div key={j.job_id} className="brut-card p-6 bg-white" data-testid={`job-${j.job_id}`}>
                  <div className="flex justify-between items-start gap-4 flex-wrap">
                    <div className="flex-1">
                      <div className="overline">{j.category_name}</div>
                      <h3 className="font-display font-bold text-2xl uppercase mt-1">{j.title}</h3>
                      <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-muted-foreground font-mono">
                        <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {j.location}, {j.zip_code}</span>
                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {j.timeline}</span>
                        {j.budget_min && <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" /> ${j.budget_min}-${j.budget_max}</span>}
                      </div>
                      {j.photos?.length > 0 && (
                        <div className="mt-3 flex gap-2">
                          {j.photos.slice(0, 5).map((p) => (
                            <img key={p} src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`} alt="" className="w-16 h-16 object-cover border-2 border-ink" />
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <span className={`inline-block font-mono uppercase tracking-widest text-xs px-2 py-1 border-2 border-ink ${j.status === "open" ? "bg-acid" : "bg-white"}`}>{j.status}</span>
                      <div className="mt-2 font-mono text-xs">{j.contractor_unlocks} pros notified</div>
                      {j.contractor_unlocks > 0 && (
                        <button onClick={() => setChatJob(j)} className="brut-btn mt-3 text-[10px] relative" data-testid={`msg-job-${j.job_id}`}>
                          <MessageCircle className="w-3 h-3 mr-1" /> Messages
                          {thread?.unread > 0 && <span className="absolute -top-2 -right-2 bg-destructive text-white text-[10px] w-5 h-5 flex items-center justify-center border-2 border-ink rounded-full">{thread.unread}</span>}
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="mt-4 text-sm text-muted-foreground line-clamp-2">{j.description}</p>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {chatJob && (
        <MessageDrawer
          jobId={chatJob.job_id}
          otherUser={chatJob.customer_id /* customer sends to whoever responds; backend filters */}
          title={chatJob.title}
          onClose={() => { setChatJob(null); load(); }}
        />
      )}
    </div>
  );
}

const Stat = ({ label, n, last }) => (
  <div className={`p-6 bg-white ${!last ? "border-r-2 border-ink" : ""}`}>
    <div className="overline">{label}</div>
    <div className="font-display font-black text-4xl mt-2">{n}</div>
  </div>
);
